import {
    Coding,
    Questionnaire,
    QuestionnaireItem,
    QuestionnaireItemEnableWhen,
    QuestionnaireResponse,
    QuestionnaireResponseItem,
    QuestionnaireResponseItemAnswer,
    ValueSet,
} from '../../types/fhir';
import { IExtentionType } from '../../types/IQuestionnareItemType';
import { AnswerLabels, getAnswerValues } from '../../components/TherapyMaterial/therapyMaterial';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const removeMd = require('remove-markdown');

export type OutlineNode =
    | {
          kind: 'group';
          linkId: string;
          text: string;
          prefix?: string;
          // 1-based number of a repeated group, undefined when the group occurs once.
          occurrence?: number;
          children: OutlineNode[];
      }
    | { kind: 'display'; linkId: string; text: string }
    | {
          kind: 'question';
          linkId: string;
          text: string;
          prefix?: string;
          // Formatted answer values; empty when the patient left the question unanswered.
          answers: string[];
          children: OutlineNode[];
      };

// The KoALA app can show these item types. Its answers to all other types are unknown, so
// conditions on them count as fulfilled (koala_app questionnaire_data.dart `_answerFor`).
const APP_QUESTION_TYPES = ['choice', 'display', 'string', 'text'];

type AnswerIndex = Map<string, QuestionnaireResponseItemAnswer[]>;

const indexResponseItems = (
    items: QuestionnaireResponseItem[] | undefined,
    byLinkId: Map<string, QuestionnaireResponseItem[]>,
): Map<string, QuestionnaireResponseItem[]> => {
    items?.forEach((item) => {
        byLinkId.set(item.linkId, [...(byLinkId.get(item.linkId) ?? []), item]);
        indexResponseItems(item.item, byLinkId);
        item.answer?.forEach((answer) => indexResponseItems(answer.item, byLinkId));
    });
    return byLinkId;
};

const indexQuestionnaireItems = (
    items: QuestionnaireItem[] | undefined,
    byLinkId: Map<string, QuestionnaireItem>,
): Map<string, QuestionnaireItem> => {
    items?.forEach((item) => {
        byLinkId.set(item.linkId, item);
        indexQuestionnaireItems(item.item, byLinkId);
    });
    return byLinkId;
};

// Mirrors EnableWhenCondition in koala_app questionnaire_model.dart: only exists, = and != on
// coded and string answers are evaluated, every other condition counts as fulfilled.
const isConditionMet = (
    condition: QuestionnaireItemEnableWhen,
    answers: QuestionnaireResponseItemAnswer[] | null,
): boolean => {
    const conditionCode = typeof condition.answerCoding?.code === 'string' ? condition.answerCoding : undefined;
    const isSupported =
        condition.operator === 'exists'
            ? condition.answerBoolean !== undefined
            : (condition.operator === '=' || condition.operator === '!=') &&
              (conditionCode !== undefined || condition.answerString !== undefined);

    if (!isSupported || answers === null) {
        return true;
    }

    if (condition.operator === 'exists') {
        return answers.length > 0 === condition.answerBoolean;
    }

    const matches = answers.some((answer) =>
        conditionCode
            ? answer.valueCoding?.code === conditionCode.code &&
              (!conditionCode.system || answer.valueCoding?.system === conditionCode.system)
            : answer.valueString !== undefined && answer.valueString.trim() === condition.answerString?.trim(),
    );
    return condition.operator === '=' ? matches : !matches;
};

const getItemText = (item: QuestionnaireItem): string => {
    if (item.text?.trim()) {
        return item.text.trim();
    }

    const markdown = item._text?.extension?.find(
        (extension) => extension.url === IExtentionType.markdown,
    )?.valueMarkdown;
    return markdown ? String(removeMd(markdown)).replace(/\\/g, '').trim() : '';
};

const findCodingDisplay = (
    questionnaire: Questionnaire,
    item: QuestionnaireItem,
    coding: Coding,
): string | undefined => {
    const matchesCoding = (candidate?: Coding): boolean =>
        Boolean(candidate && candidate.code === coding.code && (!coding.system || candidate.system === coding.system));

    const fromOptions = item.answerOption?.find((option) => matchesCoding(option.valueCoding))?.valueCoding?.display;
    if (fromOptions) {
        return fromOptions;
    }

    const valueSetId = item.answerValueSet?.startsWith('#') ? item.answerValueSet.slice(1) : undefined;
    const valueSet = questionnaire.contained?.find(
        (resource): resource is ValueSet =>
            (resource as Partial<ValueSet>).resourceType === 'ValueSet' && resource.id === valueSetId,
    );
    return valueSet?.compose?.include
        ?.flatMap((include) => include.concept ?? [])
        .find((concept) => concept.code === coding.code)?.display;
};

const formatAnswers = (
    answers: QuestionnaireResponseItemAnswer[],
    labels: AnswerLabels,
    lookupCoding?: (coding: Coding) => string | undefined,
): string[] =>
    answers
        .flatMap((answer) => {
            if (answer.valueCoding && !answer.valueCoding.display && lookupCoding) {
                return [lookupCoding(answer.valueCoding) || answer.valueCoding.code || ''];
            }
            return getAnswerValues(answer).map((value) =>
                typeof value === 'boolean' ? (value ? labels.yes : labels.no) : String(value),
            );
        })
        .filter((value) => value.trim().length > 0);

const getChildResponseItems = (responseItems: QuestionnaireResponseItem[]): QuestionnaireResponseItem[] =>
    responseItems.flatMap((responseItem) => [
        ...(responseItem.item ?? []),
        ...(responseItem.answer?.flatMap((answer) => answer.item ?? []) ?? []),
    ]);

const buildFromQuestionnaire = (
    questionnaire: Questionnaire,
    questionnaireResponse: QuestionnaireResponse,
    labels: AnswerLabels,
): OutlineNode[] => {
    const responseItemsByLinkId = indexResponseItems(questionnaireResponse.item, new Map());
    const questionnaireItemsByLinkId = indexQuestionnaireItems(questionnaire.item, new Map());
    const answersByLinkId: AnswerIndex = new Map(
        Array.from(responseItemsByLinkId.entries()).map(([linkId, items]) => [
            linkId,
            items.flatMap((item) => item.answer ?? []),
        ]),
    );

    const answersFor = (linkId: string): QuestionnaireResponseItemAnswer[] | null => {
        const item = questionnaireItemsByLinkId.get(linkId);
        if (!item || item.type === 'group' || !APP_QUESTION_TYPES.includes(item.type)) {
            return null;
        }
        return answersByLinkId.get(linkId) ?? [];
    };

    const isEnabled = (item: QuestionnaireItem): boolean => {
        if (!item.enableWhen?.length) {
            return true;
        }
        const isMet = (condition: QuestionnaireItemEnableWhen): boolean =>
            isConditionMet(condition, answersFor(condition.question));
        // The editor and the app treat a missing enableBehavior as "any".
        return item.enableBehavior === 'all' ? item.enableWhen.every(isMet) : item.enableWhen.some(isMet);
    };

    const walk = (items: QuestionnaireItem[] | undefined, scope: QuestionnaireResponseItem[]): OutlineNode[] =>
        (items ?? []).flatMap((item): OutlineNode[] => {
            // A group that is not enabled hides all of its questions.
            if (!isEnabled(item)) {
                return [];
            }

            const scoped = scope.filter((responseItem) => responseItem.linkId === item.linkId);
            // Responses that do not nest their items like the questionnaire are matched by linkId.
            const matching = scoped.length > 0 ? scoped : responseItemsByLinkId.get(item.linkId) ?? [];
            const text = getItemText(item);

            if (item.type === 'group') {
                const occurrences = matching.length > 0 ? matching : [undefined];
                return occurrences.flatMap((occurrence, index): OutlineNode[] => {
                    const children = walk(item.item, occurrence?.item ?? []);
                    return children.length > 0
                        ? [
                              {
                                  kind: 'group',
                                  linkId: item.linkId,
                                  text,
                                  prefix: item.prefix,
                                  occurrence: occurrences.length > 1 ? index + 1 : undefined,
                                  children,
                              },
                          ]
                        : [];
                });
            }

            if (item.type === 'display') {
                return text ? [{ kind: 'display', linkId: item.linkId, text }] : [];
            }

            return [
                {
                    kind: 'question',
                    linkId: item.linkId,
                    text,
                    prefix: item.prefix,
                    answers: formatAnswers(
                        matching.flatMap((responseItem) => responseItem.answer ?? []),
                        labels,
                        (coding) => findCodingDisplay(questionnaire, item, coding),
                    ),
                    children: walk(item.item, getChildResponseItems(matching)),
                },
            ];
        });

    return walk(questionnaire.item, questionnaireResponse.item ?? []);
};

// Without the questionnaire, hidden questions cannot be told apart from skipped ones, so every
// response item is listed.
const buildFromResponse = (items: QuestionnaireResponseItem[] | undefined, labels: AnswerLabels): OutlineNode[] =>
    (items ?? []).map((item): OutlineNode => {
        const children = buildFromResponse(getChildResponseItems([item]), labels);
        if (!item.answer?.length && children.length > 0) {
            return { kind: 'group', linkId: item.linkId, text: item.text?.trim() || '', children };
        }
        return {
            kind: 'question',
            linkId: item.linkId,
            text: item.text?.trim() || item.linkId,
            answers: formatAnswers(item.answer ?? [], labels),
            children,
        };
    });

export const buildQuestionnaireResponseOutline = ({
    questionnaire,
    questionnaireResponse,
    labels,
}: {
    questionnaire?: Questionnaire | null;
    questionnaireResponse: QuestionnaireResponse;
    labels: AnswerLabels;
}): OutlineNode[] =>
    questionnaire?.item?.length
        ? buildFromQuestionnaire(questionnaire, questionnaireResponse, labels)
        : buildFromResponse(questionnaireResponse.item, labels);
